import React, { useMemo } from 'react';
import { Box, CircularProgress, Paper, Typography } from '@mui/material';
import DOMPurify from 'dompurify';
import { SEARCE_COLORS } from '../../theme';
import SectionTitle from './SectionTitle';

interface ExecutiveSummaryNarrativeProps {
  paragraphs: string[];
  /** When true, shows a centered spinner (same pattern as Outcomes & Goals) while the weekly description loads. */
  loading?: boolean;
}

/**
 * Heuristic: does this string look like rich-text editor output?
 * Anything with at least one HTML tag is treated as HTML and sanitized
 * before injection. Everything else is rendered as plain text via Typography.
 */
function isHtmlContent(value: string): boolean {
  if (!value) return false;
  return /<[a-z][\s\S]*>/i.test(value);
}

const SANITIZE_CONFIG: DOMPurify.Config = {
  // Block-level + inline tags commonly emitted by rich-text editors (Quill / TipTap).
  ALLOWED_TAGS: [
    'a',
    'b',
    'blockquote',
    'br',
    'code',
    'div',
    'em',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'hr',
    'i',
    'img',
    'li',
    'mark',
    'ol',
    'p',
    'pre',
    's',
    'span',
    'strong',
    'sub',
    'sup',
    'table',
    'tbody',
    'td',
    'tfoot',
    'th',
    'thead',
    'tr',
    'u',
    'ul',
  ],
  ALLOWED_ATTR: ['href', 'target', 'rel', 'src', 'alt', 'title', 'style', 'class', 'colspan', 'rowspan'],
  // Force external links to open in a new tab + drop unsafe protocols.
  ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
};

const HtmlBlock: React.FC<{ html: string }> = ({ html }) => {
  const sanitized = useMemo(() => DOMPurify.sanitize(html, SANITIZE_CONFIG), [html]);
  return (
    <Box
      // Rendering admin-authored HTML stored in the DB. Sanitized via DOMPurify
      // immediately above; the allow-list strips event handlers, scripts, and
      // unsafe URI schemes. Safe to inject.
      dangerouslySetInnerHTML={{ __html: sanitized }}
      sx={{
        // Give nested rich-text elements sensible spacing/inheritance.
        '& p': { m: 0, mb: '8px', '&:last-child': { mb: 0 } },
        '& div': { m: 0 },
        '& ul, & ol': { pl: '20px', m: 0, mb: '8px' },
        '& li': { mb: '4px' },
        '& a': { color: SEARCE_COLORS.primary },
        '& strong, & b': { fontWeight: 600 },
        '& em, & i': { fontStyle: 'italic' },
        '& img': { maxWidth: '100%', height: 'auto' },
        '& span[style]': {
          // Some editors set their own font-family; let the page typography win.
          fontFamily: 'inherit !important',
        },
      }}
    />
  );
};

const ExecutiveSummaryNarrative: React.FC<ExecutiveSummaryNarrativeProps> = ({
  paragraphs,
  loading = false,
}) => (
  <Paper
    elevation={0}
    sx={{
      backgroundColor: '#fff',
      border: `1px solid ${SEARCE_COLORS.border}`,
      borderRadius: '10px',
      p: '20px 24px',
      boxShadow: '0 1px 4px rgba(0,0,0,.06), 0 0 0 1px rgba(0,0,0,.03)',
      mb: '20px',
    }}
  >
    <Box sx={{ display: 'block' }}>
      <SectionTitle>Executive Summary</SectionTitle>
    </Box>
    <Box
      sx={{
        fontSize: '0.84rem',
        lineHeight: 1.7,
        color: SEARCE_COLORS.textSecondary,
      }}
    >
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={32} sx={{ color: SEARCE_COLORS.primary }} />
        </Box>
      ) : (
        paragraphs.map((p, idx) => {
          const isHtml = isHtmlContent(p);
          const wrapperSx = {
            mt: idx === 0 ? 0 : '10px',
          } as const;
          if (isHtml) {
            return (
              <Box key={idx} sx={wrapperSx}>
                <HtmlBlock html={p} />
              </Box>
            );
          }
          return (
            <Typography
              key={idx}
              sx={{
                fontSize: 'inherit',
                lineHeight: 'inherit',
                color: 'inherit',
                ...wrapperSx,
              }}
            >
              {p}
            </Typography>
          );
        })
      )}
    </Box>
  </Paper>
);

export default ExecutiveSummaryNarrative;
